// Pure helpers for the owner Site Progress page (attach deliverables to a
// builder visit). Backend rules live in backend/common/site_progress.py.
import { KIND_LABEL } from '../developer/siteTimelineModel';

export const FALLBACK_KINDS = Object.keys(KIND_LABEL).map((key) => ({ key, label: KIND_LABEL[key] }));

export const EMPTY_FORM = { kind: 'orthomosaic', url: '', preview_url: '', label: '', ground_width_m: '' };

const isGeoTiff = (v) => /\.tiff?$/i.test((() => { try { return new URL(String(v || '').trim()).pathname; } catch (e) { return ''; } })());

// Ground width only matters for an orthomosaic that isn't a GeoTIFF (a GeoTIFF carries its own scale).
export function wantsGroundWidth(form) {
  return form.kind === 'orthomosaic' && !isGeoTiff(form.url);
}

// One short line on how a saved map can be measured.
export function measureLine(d) {
  if (!d) return '';
  if (d.measure) return d.measure.source === 'geotiff' ? `Measurable · ${(d.measure.pixel_m[0] * 100).toFixed(1)} cm/px${d.measure.elevation ? ' · elevation' : ''}` : 'Measurable · scale entered';
  return d.measure_note || '';
}

// One short line on whether a saved model / point cloud opens in the browser.
export function viewLine(d) {
  if (!d) return '';
  if (d.scan && d.scan.note) return d.scan.note;
  if (d.view) return d.kind === 'site_scan' ? `Opens as 3D scan · .${d.view.format}` : `Opens in 3D · .${d.view.format}`;
  return d.view_note || '';
}

function isHttps(v) {
  try {
    const u = new URL(String(v || '').trim());
    return u.protocol === 'https:' && !!u.host;
  } catch (e) {
    return false;
  }
}

// null when the form can be sent; otherwise one short reason.
export function validateForm(form, kinds) {
  const keys = (kinds && kinds.length ? kinds : FALLBACK_KINDS).map((k) => k.key);
  if (!keys.includes(form.kind)) return 'Pick a deliverable type.';
  if (!isHttps(form.url)) return 'Link must start with https://';
  if (String(form.preview_url || '').trim() && !isHttps(form.preview_url)) return 'Preview must start with https://';
  if (String(form.label || '').trim().length > 120) return 'Label is too long.';
  const gw = String(form.ground_width_m || '').trim();
  if (gw && wantsGroundWidth(form) && !(Number(gw) > 0 && Number(gw) <= 100000)) return 'Ground width must be a number of metres.';
  return null;
}

// The link field takes a finished scan or the walk-through video that becomes one.
export function linkPlaceholder(kind) {
  return kind === 'site_scan' ? 'https:// link to the scan (.spz, .ply, .splat) or walk-through video' : 'https:// link to the file';
}

export function buildPayload(form) {
  const item = { kind: form.kind, url: String(form.url).trim() };
  const preview = String(form.preview_url || '').trim();
  const label = String(form.label || '').trim();
  if (preview) item.preview_url = preview;
  if (label) item.label = label;
  const gw = String(form.ground_width_m || '').trim();
  if (gw && wantsGroundWidth(form)) item.ground_width_m = Number(gw);
  return { deliverables: [item] };
}

export function ownerStats(visits, unlinked) {
  const v = visits || [];
  const delivered = v.filter((x) => (x.deliverables || []).length || x.media_delivery_url).length;
  return {
    visits: v.length,
    delivered,
    awaiting: v.filter((x) => x.status !== 'failed' && !(x.deliverables || []).length && !x.media_delivery_url).length,
    unlinked: (unlinked || []).length,
  };
}

export function filterVisits(visits, tab) {
  const v = visits || [];
  if (tab === 'awaiting') return v.filter((x) => x.status !== 'failed' && !(x.deliverables || []).length && !x.media_delivery_url);
  if (tab === 'delivered') return v.filter((x) => (x.deliverables || []).length || x.media_delivery_url);
  return v;
}

export function syncNote(sync) {
  if (!sync) return '';
  if (sync.error) return sync.error;
  const parts = [];
  if (sync.visits_created) parts.push(`${sync.visits_created} new visit${sync.visits_created === 1 ? '' : 's'} linked`);
  if (sync.projects_created) parts.push(`${sync.projects_created} new project${sync.projects_created === 1 ? '' : 's'}`);
  if (sync.errors) parts.push(`${sync.errors} booking${sync.errors === 1 ? '' : 's'} failed to link`);
  return parts.join(' · ');
}

export function unlinkedReason(b) {
  return b && b.has_builder_account ? 'Waiting to link' : 'No builder account for this email';
}
